import { Field, FieldArray, getIn, useFormikContext } from "formik";
import React, { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "../../scss/delivery-note-documentation.scss";
import { formatDate } from "../../utils/dateFormat";
import DocumentationImportWizard from "./DocumentationImportWizard";
import {
  defaultDocLeafValue,
  docLeaves,
  genRowId,
  getDocFieldWidget,
} from "./utils";

const HIGHLIGHT_CLASS = "bg-yellow-100 bg-opacity-25";

function LeafInput({ leaf, field, errors, highlight = false }) {
  const name = `${field}.${leaf.id}`;
  const hasError = !!getIn(errors, name);
  const errorClass = hasError ? "is-invalid" : "";
  const highlightClass = highlight ? HIGHLIGHT_CLASS : "";
  // For form-check inputs (radio/checkbox) the background-color renders the
  // checked indicator, so the highlight tint must sit on a wrapping element —
  // applying it to the input itself hides the dot/check.
  const inputClass = `${errorClass} ${highlightClass}`.trim();
  const checkInputClass = errorClass;
  const checkWrapperClass = highlightClass;
  const widget = getDocFieldWidget(leaf);

  if (widget === "boolean") {
    return (
      <div className={`form-check ${checkWrapperClass}`.trim()}>
        <Field
          type="checkbox"
          name={name}
          className={`form-check-input ${checkInputClass}`.trim()}
        />
      </div>
    );
  }

  if (widget === "select") {
    return (
      <Field as="select" name={name} className={`form-select ${inputClass}`.trim()}>
        {(leaf.enum || []).map((c, i) => (
          <option key={i} value={c}>
            {c}
          </option>
        ))}
      </Field>
    );
  }

  if (widget === "radio") {
    return (
      <div className={`d-flex flex-wrap gap-3 ${checkWrapperClass}`.trim()}>
        {(leaf.enum || []).map((choice, i) => (
          <label key={i} className="form-check form-check-inline mb-0">
            <Field
              type="radio"
              name={name}
              value={choice}
              className={`form-check-input ${checkInputClass}`.trim()}
            />
            <span className="form-check-label ms-1">{choice}</span>
          </label>
        ))}
      </div>
    );
  }

  if (widget === "number") {
    return (
      <div className="input-group">
        <Field type="number" name={name} className={`form-control ${inputClass}`.trim()} />
        {leaf["x-unit"] && <span className="input-group-text">{leaf["x-unit"]}</span>}
      </div>
    );
  }

  if (widget === "date") {
    return <Field type="date" name={name} className={`form-control ${inputClass}`.trim()} />;
  }

  return <Field type="text" name={name} className={`form-control ${inputClass}`.trim()} />;
}

function ColumnNode({ col, field, errors, highlights }) {
  if (col.kind === "group") {
    return (
      <div className="border-start border-3 ps-3 d-flex flex-column gap-2">
        <div className="text-uppercase small text-muted fw-semibold">{col.title}</div>
        {(col.columns || []).map((child) => (
          <ColumnNode
            key={child.id || child.title}
            col={child}
            field={field}
            errors={errors}
            highlights={highlights}
          />
        ))}
      </div>
    );
  }

  return (
    <div>
      <label className="form-label mb-1" htmlFor={`${field}.${col.id}`}>
        {col.title}
      </label>
      <LeafInput
        leaf={col}
        field={field}
        errors={errors}
        highlight={!!highlights?.[col.id]}
      />
      {col.description && (
        <small className="text-muted d-block mt-1">{col.description}</small>
      )}
    </div>
  );
}

function DocumentFieldsSection({ field, documentColumns, errors }) {
  const { t } = useTranslation();
  if (!documentColumns?.length) return null;

  return (
    <div className="border rounded p-3 mb-3">
      <div className="text-uppercase small text-muted fw-semibold mb-3">
        {t("delivery_notes.templates.document_fields")}
      </div>
      {documentColumns.map((col) => {
        if (getDocFieldWidget(col) === "boolean") {
          const label = col.description || col.title;
          const name = `${field}.document_values.${col.id}`;
          return (
            <div key={col.id} className="form-check mb-2 d-flex align-items-start gap-2">
              <Field
                type="checkbox"
                name={name}
                id={name}
                className="form-check-input mt-1 flex-shrink-0"
              />
              <label className="form-check-label" htmlFor={name}>
                {label}
              </label>
            </div>
          );
        }
        return (
          <div key={col.id} className="mb-2">
            <label className="form-label mb-1" htmlFor={`${field}.document_values.${col.id}`}>
              {col.title || col.description}
            </label>
            <LeafInput
              leaf={col}
              field={`${field}.document_values`}
              errors={errors}
            />
            {col.title && col.description && (
              <small className="text-muted d-block mt-1">{col.description}</small>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ImportedBadge({ importedFrom }) {
  const { t } = useTranslation();
  const parts = [];
  if (importedFrom.filename) parts.push(importedFrom.filename);
  if (importedFrom.at) parts.push(formatDate(importedFrom.at));
  if (importedFrom.by?.name) parts.push(importedFrom.by.name);
  return (
    <span
      className="badge bg-info bg-opacity-25 text-info-emphasis fw-normal"
      title={parts.join(" · ")}
    >
      <i className="fa fa-file-import me-1" />
      {t("delivery_notes.templates.import_excel.imported_badge", {
        details: parts.join(" · "),
      })}
    </span>
  );
}

function DeliveryNoteDocumentationForm({
  field,
  schema = { row_label: "", columns: [], document_columns: [] },
  templateValues = { rows: [] },
  setFieldValue,
  values = { rows: [], document_values: {} },
  errors = {},
}) {
  const { t } = useTranslation();
  const { initialValues } = useFormikContext() || {};
  const columns = schema.columns || [];
  const documentColumns = schema.document_columns || [];
  const leaves = docLeaves(columns);
  const rowLabel = schema.row_label || t("common.position");

  const emptyRow = Object.fromEntries(
    leaves.map((l) => [l.id, defaultDocLeafValue(l)]),
  );
  const resetRow = templateValues.rows?.[0] || emptyRow;
  const rows = values.rows || [];

  // Snapshot of rows as they were initially loaded (saved from server or
  // copied from the source note), keyed by stable row id. Used to highlight
  // unchanged loaded values vs. values the user has typed this session.
  const initialById = useMemo(() => {
    const m = new Map();
    const initialRows = getIn(initialValues, `${field}.rows`) || [];
    initialRows.forEach((r) => {
      if (r?._id) m.set(r._id, r);
    });
    return m;
  }, [initialValues, field]);

  const computeHighlights = (row) => {
    const initial = row?._id ? initialById.get(row._id) : null;
    if (!initial) return {};
    const out = {};
    leaves.forEach((leaf) => {
      const v = row?.[leaf.id];
      if (v !== initial[leaf.id]) return;
      if (v === "" || v === null || v === undefined || v === false) return;
      out[leaf.id] = true;
    });
    return out;
  };

  // Bumping the per-row token changes the inputs' keys, which remounts every
  // input in the row — discarding browser-held state like Firefox's badInput
  // buffer for `<input type="number">` after typing non-numeric characters.
  const [clearTokens, setClearTokens] = useState({});

  const clearRow = (rowIndex) => {
    setFieldValue(`${field}.rows.${rowIndex}`, {
      ...resetRow,
      _id: rows[rowIndex]?._id || genRowId(),
    });
    setClearTokens((prev) => ({
      ...prev,
      [rowIndex]: (prev[rowIndex] || 0) + 1,
    }));
  };

  const [importOpen, setImportOpen] = useState(false);
  const hasExistingDocData = columns.length > 0 || rows.length > 0;

  // The wizard returns the full column tree for both modes: replace mode
  // builds new text columns, fill mode returns the existing fields (with
  // select/radio options extended by unknown imported values).
  const handleImport = ({ columns: newColumns, rows: newRows, importedFrom }) => {
    setFieldValue("documentation_schema", {
      ...schema,
      columns: newColumns,
      imported_from: importedFrom,
    });
    setFieldValue(field, {
      rows: newRows,
      document_values: values.document_values || {},
    });
    setImportOpen(false);
  };

  return (
    <FieldArray
      name={`${field}.rows`}
      render={(arrayHelpers) => (
        <>
        <div className="card border-0 mb-4">
          <div className="card-header h6 mb-0 bg-none p-3 d-flex align-items-center flex-wrap gap-2">
            <i className="fa fa-book fa-lg fa-fw text-dark text-opacity-50 me-1" />{" "}
            {t("menu.documentation")}
            {schema.imported_from && (
              <ImportedBadge importedFrom={schema.imported_from} />
            )}
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary ms-auto"
              onClick={() => setImportOpen(true)}
            >
              <i className="fa fa-file-import me-1" />
              {t("delivery_notes.templates.import_excel.button")}
            </button>
          </div>
          <div className="card-body">
            {columns.length > 0 && rows.length === 0 && (
              <div className="text-center text-muted py-3">
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={() => arrayHelpers.push({ ...emptyRow, _id: genRowId() })}
                >
                  <i className="fa fa-plus me-1" />
                  {t("delivery_notes.templates.add_row")}
                </button>
              </div>
            )}
            {rows.map((row, rowIndex) => {
              const highlights = computeHighlights(row);
              return (
                <Fragment key={row._id || `row-${rowIndex}`}>
                  <div className="border rounded p-3 mb-3">
                    <div className="d-flex align-items-center mb-3">
                      <span className="badge bg-secondary me-2">
                        {rowLabel} {rowIndex + 1}
                      </span>
                      <div className="ms-auto btn-group">
                        <button
                          type="button"
                          className="btn btn-sm btn-success"
                          onClick={() =>
                            arrayHelpers.insert(rowIndex + 1, {
                              ...rows[rowIndex],
                              _id: genRowId(),
                            })
                          }
                        >
                          <i className="fa fa-plus" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-warning"
                          onClick={() => clearRow(rowIndex)}
                        >
                          <i className="fa fa-eraser" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => arrayHelpers.remove(rowIndex)}
                          disabled={rows.length === 1}
                        >
                          <i className="fa fa-minus" />
                        </button>
                      </div>
                    </div>
                    <div className="dn-doc-columns" key={`fields-${clearTokens[rowIndex] || 0}`}>
                      {columns.map((col, colIndex) => (
                        <ColumnNode
                          key={col.id || `g-${colIndex}`}
                          col={col}
                          field={`${field}.rows.${rowIndex}`}
                          errors={errors}
                          highlights={highlights}
                        />
                      ))}
                    </div>
                  </div>
                </Fragment>
              );
            })}
            <DocumentFieldsSection
              field={field}
              documentColumns={documentColumns}
              errors={errors}
            />
          </div>
        </div>
        {importOpen && (
          <DocumentationImportWizard
            hasExistingData={hasExistingDocData}
            hasExistingRows={rows.length > 0}
            schema={schema}
            onCancel={() => setImportOpen(false)}
            onImport={handleImport}
          />
        )}
        </>
      )}
    />
  );
}

export default DeliveryNoteDocumentationForm;
